import React, { useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Text,
  Modal,
  ButtonGroup,
  Panel,
  PanelGroup,
  PanelLeft,
  Button,
  designSystem,
} from '@yola/ws-ui';
import bowser from 'yola-bowser';
import { context as viewportContext } from 'yola-editor/src/js/utils/viewport-provider';
import constants from '../constants';
import SeoSection from './seo-section';
import GeneralSection from './general-section';
import CustomCodeSection from './custom-code-section';

const { Tabs, TabsList, Tab, TabsContentList, TabContent } = designSystem;
const { sizes, tabIdentifiers } = constants;

const PageSettings = ({
  activeTabId,
  control,
  fieldRefs,
  captions,
  pageId,
  pathPrefix,
  businessName,
  languageCodeOptions,
  isHomePage,
  isDisplayInNavigationAvailable,
  isPasswordProtected,
  isPasswordProtectedPagesFeatureAvailable,
  isPasswordProtectedPagesFeatureFlagEnabled,
  isBusinessInfoInWebsiteSettingsFeatureFlagEnabled,
  isHeadCodeDisabled,
  isHeaderCodeDisabled,
  isFooterCodeDisabled,
  shouldConfirmExitDisplayed,
  onChangePageName,
  onChangeSameAsPageName,
  onEnablePasswordProtection,
  onDisablePasswordProtection,
  onCodeEditorFocus,
  onHeadCodeUpgradeClick,
  onHeaderCodeUpgradeClick,
  onFooterCodeUpgradeClick,
  onTabChange,
  onSubmit,
  onCancel,
  onKeyDown,
  onConfirmExitSubmit,
  onConfirmExitCancel,
}) => {
  const { width, height } = useContext(viewportContext);
  const modalWidth =
    width >= sizes.LARGE_RES_WIDTH ? sizes.MODAL_LARGE_WIDTH : sizes.MODAL_MEDIUM_WIDTH;
  const modalHeight =
    height >= sizes.LARGE_RES_HEIGHT ? sizes.MODAL_LARGE_HEIGHT : sizes.MODAL_MEDIUM_HEIGHT;

  const scrollContainerRef = useRef();

  return (
    <div className="ws-page-settings">
      <Modal
        width={modalWidth}
        height={modalHeight}
        className="ws-page-settings__modal"
        dragHandleSelector=".ws-page-settings__header"
        promptCaptions={captions.confirmExit}
        overlay="visible"
        centered
        draggable={false}
        resizable={false}
        fullscreen={bowser.mobile}
        showPromptForce={shouldConfirmExitDisplayed}
        onKeyDown={onKeyDown}
        handleSubmit={onConfirmExitSubmit}
        handleCancel={onConfirmExitCancel}
      >
        <PanelGroup height="100%">
          <Panel
            theme="gray-100"
            height={sizes.CONTROL_PANEL_HEIGHT}
            align="middle"
            corners="squared"
            className="ws-panel--shrink ws-page-settings__header"
            style={{ borderBottom: 'none' }}
          >
            <PanelLeft style={{ paddingLeft: '24px' }}>
              <Text type="heading-6">{captions.pageSettings}</Text>
            </PanelLeft>
          </Panel>
          <div className="ws-page-settings__tabs">
            <Tabs
              activeTabId={activeTabId}
              onTabChange={onTabChange}
              scrollContainerRef={scrollContainerRef}
            >
              <TabsList>
                <Tab tabId={tabIdentifiers.GENERAL} label={captions.general.tabLabel} />
                <Tab tabId={tabIdentifiers.SEO} label={captions.seoSettings.tabLabel} />
                <Tab tabId={tabIdentifiers.CUSTOM_CODE} label={captions.customCode.tabLabel} />
              </TabsList>
              <TabsContentList>
                <TabContent tabId={tabIdentifiers.GENERAL}>
                  <GeneralSection
                    control={control}
                    fieldRefs={fieldRefs}
                    captions={captions.general}
                    pageId={pageId}
                    pathPrefix={pathPrefix}
                    languageCodeOptions={languageCodeOptions}
                    isPathDisabled={isHomePage}
                    isDisplayInNavigationAvailable={isDisplayInNavigationAvailable}
                    isPasswordProtected={isPasswordProtected}
                    isPasswordProtectedPagesFeatureAvailable={
                      isPasswordProtectedPagesFeatureAvailable
                    }
                    isPasswordProtectedPagesFeatureFlagEnabled={
                      isPasswordProtectedPagesFeatureFlagEnabled
                    }
                    onChangePageName={onChangePageName}
                    onEnablePasswordProtection={onEnablePasswordProtection}
                    onDisablePasswordProtection={onDisablePasswordProtection}
                  />
                </TabContent>
                <TabContent tabId={tabIdentifiers.SEO}>
                  <SeoSection
                    control={control}
                    fieldRefs={fieldRefs}
                    captions={captions.seoSettings}
                    pathPrefix={pathPrefix}
                    businessName={businessName}
                    isBusinessInfoInWebsiteSettingsFeatureFlagEnabled={
                      isBusinessInfoInWebsiteSettingsFeatureFlagEnabled
                    }
                    onChangeSameAsPageName={onChangeSameAsPageName}
                  />
                </TabContent>
                <TabContent tabId={tabIdentifiers.CUSTOM_CODE}>
                  <CustomCodeSection
                    control={control}
                    fieldRefs={fieldRefs}
                    captions={captions.customCode}
                    scrollContainerRef={scrollContainerRef}
                    isHeadCodeDisabled={isHeadCodeDisabled}
                    isHeaderCodeDisabled={isHeaderCodeDisabled}
                    isFooterCodeDisabled={isFooterCodeDisabled}
                    onCodeEditorFocus={onCodeEditorFocus}
                    onHeadCodeUpgradeClick={onHeadCodeUpgradeClick}
                    onHeaderCodeUpgradeClick={onHeaderCodeUpgradeClick}
                    onFooterCodeUpgradeClick={onFooterCodeUpgradeClick}
                  />
                </TabContent>
              </TabsContentList>
            </Tabs>
          </div>
          <Panel
            height={sizes.CONTROL_PANEL_HEIGHT}
            align="middle"
            corners="squared"
            className="ws-panel--shrink ws-page-settings__footer"
          >
            <ButtonGroup>
              <Button
                className="ws-page-settings__button ws-page-settings__button--apply"
                size="large"
                type="default"
                stretch="block"
                onClick={onSubmit}
              >
                {captions.apply}
              </Button>
              <Button
                className="ws-page-settings__button"
                size="large"
                type="default"
                stretch="block"
                onClick={onCancel}
              >
                {captions.cancel}
              </Button>
            </ButtonGroup>
          </Panel>
        </PanelGroup>
      </Modal>
    </div>
  );
};

PageSettings.propTypes = {
  activeTabId: PropTypes.string.isRequired,
  control: PropTypes.shape().isRequired,
  fieldRefs: PropTypes.shape().isRequired,
  captions: PropTypes.shape().isRequired,
  pageId: PropTypes.string.isRequired,
  pathPrefix: PropTypes.string.isRequired,
  businessName: PropTypes.string.isRequired,
  languageCodeOptions: PropTypes.array.isRequired,
  isHomePage: PropTypes.bool.isRequired,
  isDisplayInNavigationAvailable: PropTypes.bool.isRequired,
  isPasswordProtected: PropTypes.bool.isRequired,
  isPasswordProtectedPagesFeatureAvailable: PropTypes.bool.isRequired,
  isPasswordProtectedPagesFeatureFlagEnabled: PropTypes.bool.isRequired,
  isBusinessInfoInWebsiteSettingsFeatureFlagEnabled: PropTypes.bool.isRequired,
  isHeadCodeDisabled: PropTypes.bool.isRequired,
  isHeaderCodeDisabled: PropTypes.bool.isRequired,
  isFooterCodeDisabled: PropTypes.bool.isRequired,
  shouldConfirmExitDisplayed: PropTypes.bool.isRequired,
  onChangePageName: PropTypes.func.isRequired,
  onChangeSameAsPageName: PropTypes.func.isRequired,
  onEnablePasswordProtection: PropTypes.func.isRequired,
  onDisablePasswordProtection: PropTypes.func.isRequired,
  onCodeEditorFocus: PropTypes.func.isRequired,
  onHeadCodeUpgradeClick: PropTypes.func.isRequired,
  onHeaderCodeUpgradeClick: PropTypes.func.isRequired,
  onFooterCodeUpgradeClick: PropTypes.func.isRequired,
  onTabChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func.isRequired,
  onConfirmExitSubmit: PropTypes.func.isRequired,
  onConfirmExitCancel: PropTypes.func.isRequired,
};

export default PageSettings;
